import * as React from "react";
import { useEffect, useState } from "react";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import { getValidationSchema, initialValues } from "./config";
import { Box, FormLabel, MenuItem, Typography } from "@mui/material";
import { StyledFormicError } from "./styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import { ICreateOrUpdateUserCredentials, ICreateUserResult } from "types/auth";
import { useAppDispatch } from "hooks/useAppDispatch";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import { ROLES, User } from "types/user";
import { registerThunk, updateUserThunk } from "reduxStore/auth/thunks";
import {
  useUpdateUserMutation,
  useAddUserMutation,
  useDeleteUserTagByTagAndUserMutation,
  useGetOrganizationsQuery,
  useAddUserTagsMutation,
} from "graphql/__generated__/hooks";
import { Modal } from "components/Modal";
import { VisuallyHiddenInput } from "components/Input";
import { Img } from "components/Img";
import { Person, Upload } from "@mui/icons-material";
import { TagManager } from "components/TagManager";
import { Tag } from "types/tag";
import { Messages, NotificationSeverity } from "helpers/constants";
import { uploadFile } from "reduxStore/files/thunks";
import { FieldWithEntitySelector } from "components/FieldWithEntitySelector";
import { Organization } from "types/organization";
import { getNewEntitiesIds } from "utils/FilterEntities";
import { useNotifications } from "hooks/useNotifications";
import { isRunningInNativeApp, isMobile } from "utils/UserDevice";
import { normalizeEmail } from "utils/email";

export type Props = {
  createUserModal: boolean;
  handleCreateUserModalDismiss: () => void;
  handleCreateOrEditUserChange: () => string | void;
  allowedRoles: ROLES[];
  isEditing?: boolean;
  userToEdit?: User;
};

const CreateOrEditUserModal = ({
  createUserModal,
  handleCreateUserModalDismiss,
  handleCreateOrEditUserChange,
  allowedRoles,
  isEditing,
  userToEdit,
}: Props) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [updateUser] = useUpdateUserMutation();
  const [createUser] = useAddUserMutation();
  const [errorImage, setErrorImage] = useState("");
  const [avatar, setAvatar] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [originalTags, setOriginalTags] = useState<Tag[]>([]);
  const [addUserTags] = useAddUserTagsMutation();
  const [deleteUserTag] = useDeleteUserTagByTagAndUserMutation();
  const { data: dataOrganization } = useGetOrganizationsQuery();
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const organizations = React.useMemo(
    () => dataOrganization?.organization || [],
    [dataOrganization],
  );
  const { notify } = useNotifications();

  useEffect(() => {
    const currentTags = userToEdit?.tags.map((tag) => tag.tag) || [];
    setTags(currentTags);
    setOriginalTags(currentTags);
  }, [userToEdit]);

  useEffect(() => {
    if (userToEdit?.org_id && organizations.length > 0) {
      const selectedOrganizationData = organizations.find(
        (org) => org.org_id === userToEdit?.org_id,
      );
      selectedOrganizationData &&
        setSelectedOrganization(selectedOrganizationData as Organization);
    }
  }, [userToEdit, organizations]);

  const handleCreateUser = async (
    reqValues: ICreateOrUpdateUserCredentials,
    picture_url: string | null,
  ) => {
    setLoading(true);
    reqValues.sendInvite = true;
    const result = await dispatch(registerThunk(reqValues));
    const payload = result.payload as ICreateUserResult;
    if (payload.status && payload.status === "success") {
      reqValues.user_id = payload.user_id;
      if (picture_url) reqValues.picture_url = picture_url;
      let data = { ...reqValues, org_id: selectedOrganization?.org_id ?? null };
      await createUser({
        variables: data as User,
      });
    } else {
      notify({
        message: Messages.errorUserCreated,
        severity: NotificationSeverity.error,
      });
      return;
    }
    handleCreateOrEditUserChange();
    createTags(reqValues.user_id as string).then();
    setLoading(false);
    notify({
      message: Messages.successUserCreated,
      severity: NotificationSeverity.success,
    });
  };

  const onUploadFile = (event: React.BaseSyntheticEvent) => {
    const file = event.target.files[0];
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      setErrorImage("Invalid file type: only PNG and JPEG are allowed");
      return;
    }
    if (file.size > 1024 * 1024 * 10) {
      setErrorImage("File size is too large: max size is 10MB");
      return;
    }
    if (errorImage !== "") {
      setErrorImage("");
    }
    setAvatar(URL.createObjectURL(file));
    setAvatarFile(file);
  };

  const UploadFile = async () => {
    if (avatarFile) {
      setUploading(true);
      const result = await dispatch(uploadFile(avatarFile));
      if (result.meta.requestStatus === "rejected") {
        setErrorImage(Messages.errorImageUpload);
        return "ERROR";
      }
      setUploading(false);
      return result?.payload?.filePath;
    } else {
      return null;
    }
  };

  const handleEditUser = async (
    reqValues: ICreateOrUpdateUserCredentials,
    picture_url: string | null,
  ) => {
    setLoading(true);
    const { payload } = await dispatch(updateUserThunk(reqValues));
    if (payload === "success") {
      if (picture_url) reqValues.picture_url = picture_url;
      let data = { ...reqValues, org_id: selectedOrganization?.org_id ?? null };
      await updateUser({
        variables: { id: reqValues.user_id, user: data as User },
      });
      notify({
        message: Messages.successUserUpdated,
        severity: NotificationSeverity.success,
      });
    } else {
      notify({
        message: Messages.errorUserUpdated,
        severity: NotificationSeverity.error,
      });
    }
    editTags(userToEdit?.user_id as string).then();
    handleCreateOrEditUserChange();
    setLoading(false);
  };

  const handleAddTag = async (tag: Tag) => {
    setTags([...tags, tag]);
  };

  const handleDeleteTag = async (tagToRemove: Tag) => {
    const newTags = tags.filter((tag) => tag.tag_id !== tagToRemove.tag_id);
    setTags(newTags);
  };

  const createTags = async (userId: string) => {
    if (tags.length === 0) return;
    const userTags = tags.map((tag) => ({
      tag_id: tag.tag_id,
      user_id: userId,
    }));
    await addUserTags({
      variables: { objects: userTags },
    });
  };

  const editTags = async (userId: string) => {
    const newTagsIds = getNewEntitiesIds(
      originalTags,
      tags,
      "tag_id",
    ) as string[];
    const newTagsData = newTagsIds.map((tagId) => ({
      user_id: userId,
      tag_id: tagId,
    }));
    if (newTagsData.length > 0) {
      await addUserTags({
        variables: {
          objects: newTagsData,
        },
      });
    }
    const removedRequirementTagsIds = getNewEntitiesIds(
      tags,
      originalTags,
      "tag_id",
    ) as string[];
    if (!removedRequirementTagsIds) return;
    for (const tagId of removedRequirementTagsIds) {
      await deleteUserTag({
        variables: { tagId, userId },
      });
    }
  };

  const formik = useFormik({
    initialValues: userToEdit || initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const reqValues = {
        user_id: userToEdit?.user_id,
        name: values.name.trim(),
        email: normalizeEmail(values.email),
        role: values.role,
      };
      const picture_url = await UploadFile();
      if (picture_url === "ERROR") return;
      if (isEditing) {
        handleEditUser(
          reqValues as ICreateOrUpdateUserCredentials,
          picture_url,
        ).then();
      } else {
        handleCreateUser(
          reqValues as ICreateOrUpdateUserCredentials,
          picture_url,
        ).then();
      }
    },
  });

  return (
    <Modal
      open={createUserModal}
      onClose={handleCreateUserModalDismiss}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      title={isEditing ? "Update User Profile" : "Add New User"}
      size="small"
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
            pt={1}
          >
            <Box>
              <Img src={avatar} width={80} height={80} icon={Person} circle />
            </Box>
            <Box ml={2}>
              <Box mb={1} fontWeight={600}>
                Add Profile Picture
              </Box>
              {isMobile() || isRunningInNativeApp() ? (
                <Typography variant="body2" color="textSecondary">
                  To change profile picture, please use a desktop device.
                </Typography>
              ) : (
                <Button
                  disabled={uploading}
                  component="label"
                  role={undefined}
                  variant="outlined"
                  tabIndex={-1}
                  startIcon={<Upload />}
                >
                  Upload image
                  <VisuallyHiddenInput type="file" onChange={onUploadFile} />
                </Button>
              )}
              {errorImage !== "" && (
                <Box mt={1} sx={{ color: "red" }}>
                  {errorImage}
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 0 }}>
            <TextField
              variant="standard"
              onChange={formik.handleChange}
              margin="dense"
              required
              fullWidth
              name="name"
              label="Name"
              type="text"
              id="name"
              value={formik?.values?.name}
            />
            <ErrorMessage name="firstname" component={StyledFormicError} />

            <TextField
              variant="standard"
              onChange={formik.handleChange}
              margin="dense"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              disabled={isEditing}
              value={formik?.values?.email}
            />
            <ErrorMessage name="email" component={StyledFormicError} />

            <TextField
              variant="standard"
              onChange={formik.handleChange}
              margin="dense"
              required
              fullWidth
              id="role"
              name="role"
              select
              label="Role"
              autoComplete="role"
              autoFocus
              color={"primary"}
              value={formik?.values?.role}
            >
              {allowedRoles.map((role: ROLES) => (
                <MenuItem
                  key={role}
                  value={role}
                  color={"primary"}
                  sx={{ backgroundColor: "primary" }}
                >
                  {role.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
            <ErrorMessage name="role" component={StyledFormicError} />
            <Box mt={2}>
              <FormLabel>Tags</FormLabel>
              <TagManager
                tags={tags}
                onAdd={handleAddTag}
                onDelete={handleDeleteTag}
                isEditing
              />
            </Box>
            <Box mt={2}>
              <FieldWithEntitySelector
                label="Organization"
                idField="org_id"
                entityName="organization"
                element={selectedOrganization}
                onChangeElement={(selected: Organization) =>
                  setSelectedOrganization(selected)
                }
                entityList={organizations}
                displayField="name"
                compact
              />
            </Box>
            <Grid sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Box mr={2}>
                <Button
                  variant="outlined"
                  onClick={handleCreateUserModalDismiss}
                >
                  Cancel
                </Button>
              </Box>

              <Box>
                <Button
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Grid>
          </Box>
        </form>
      </FormikProvider>
    </Modal>
  );
};

export default CreateOrEditUserModal;
